import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
// Assets
import images from '../../../assets/images/images';
// Components
import { Button, Image, CustomLink, Input } from '../..';
// Styles
import styles from './contactPointSelector.module.scss';
import { IContactPoint, IContactPointViewItem, IMessageRecipient } from '../../../interfaces';
import { getContactGroupsAndContactPointsByOrganisationId } from '../../../services';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../redux';
import { MailboxOwnerType, Targets } from '../../../enums';

interface IContactPointSelectorProps {
    recipientList: {
        name: string;
        fullName: string;
        contactPointId?: string;
        contactRecipientDetails?: IMessageRecipient;
        organisationId?: string;
        iscategoryPending?: boolean;
        contactPoint?: IContactPoint;
    }[];
    onSelectContactPoint: (event?: React.MouseEvent) => void;
    onRemoveContactPoint: (contactPointIndex: number, isCategory?: boolean) => void;
    allowMultiple: boolean;
    isFromForward?: boolean;
    isSelectRecipient?: boolean;
    selectedContactStack?: IContactPointViewItem[];
    setSelectCategory?: () => void;
    organisationId?: string;
}

export const ContactPointSelector = (props: IContactPointSelectorProps): JSX.Element => {
    const { formatMessage } = useIntl();
    const [isCategoryRemoved, setIsCategoryRemoved] = useState(false);

    const { mailbox } = useSelector(({ mailbox }: IRootState) => ({ mailbox: mailbox.mailbox }));

    const getTarget = useCallback((): Targets => {
        switch (mailbox.ownerType) {
            case MailboxOwnerType.ORGANISATION:
                return Targets.COMPANY;

            case MailboxOwnerType.CITIZEN:
            default:
                return Targets.CITIZEN;
        }
    }, [mailbox.ownerType]);

    const generateCategoryRow = (): JSX.Element => {
        if (props.selectedContactStack?.length === 0) {
            return <></>;
        }
        let category = '';
        props.selectedContactStack.slice(1).forEach((item) => {
            category += item.name + ' / ';
        });

        return (
            <span>
                {category}
                <b>{props.recipientList?.[0]?.name}</b>
            </span>
        );
    };

    const generateRecipientRow = ({ fullName }: { fullName: string; contactPointId?: string }): JSX.Element => (
        <div className="text-truncate">
            <span title={fullName}>{fullName}</span>
        </div>
    );

    const onChooseClick = (): void => {
        if (isCategoryRemoved) {
            props.onRemoveContactPoint(0, true);
            return;
        }
        props.onSelectContactPoint();
    };

    const onCategoryRemove = (): void => {
        setIsCategoryRemoved(true);
        props.setSelectCategory();
    };

    const getRecipientData = (isCategory?: boolean): JSX.Element | string => {
        if (props.recipientList && props.recipientList[0]?.organisationId && props.selectedContactStack?.length === 0) {
            isCategory && setIsCategoryRemoved(true);
            return isCategory ? '' : props.recipientList[0]?.name;
        }
        if (props.selectedContactStack?.length === 0) {
            return isCategory
                ? props.recipientList[0]?.contactRecipientDetails?.contactPoint?.label
                : props.recipientList[0]?.contactRecipientDetails?.label;
        } else {
            return isCategory ? generateCategoryRow() : props.selectedContactStack?.[0]?.name;
        }
    };

    const getNewMailRecipient = (isCategory?: boolean): JSX.Element => {
        return (
            <>
                <div className={styles.selectedRecipient}>
                    <div className={styles.SelectLabel}>
                        {isCategory ? (
                            <FormattedMessage id="CHOOSE_SEARCH_CATEGORY" values={{ text: '' }} />
                        ) : (
                            <FormattedMessage id="CITIZEN" />
                        )}
                    </div>
                    <div className={styles.selectedSection}>
                        <span>{getRecipientData(isCategory)}</span>
                        <CustomLink
                            onClick={(): void => {
                                isCategory ? onCategoryRemove() : props.onRemoveContactPoint(0);
                            }}
                            title={formatMessage({ id: 'DELETE' })}
                        >
                            <Image src={images.close} alt="DELETE" />
                        </CustomLink>
                    </div>
                </div>
            </>
        );
    };

    const showSelectInput = !(!props.allowMultiple && props.recipientList?.length === 1) && !props.isSelectRecipient;
    const recipientRowList = props.recipientList.map((contactPoint, index) => (
        <div key={'contactPointDiv' + index} className="row align-items-center p-0 m-2">
            <div
                className={`w-100 d-flex justify-content-between pb-2 ${
                    showSelectInput || (props.recipientList.length > 1 && index < props.recipientList.length - 1)
                        ? 'border-bottom'
                        : ''
                }`}
            >
                {contactPoint?.fullName !== null ? (
                    <div className="text-truncate col-sm-11 pl-0">{generateRecipientRow(contactPoint)}</div>
                ) : (
                    <div className="text-truncate">
                        <span title={contactPoint?.name}>{contactPoint?.name}</span>
                    </div>
                )}
                <CustomLink
                    onClick={(): void => {
                        props.onRemoveContactPoint(index);
                    }}
                    title={formatMessage({ id: 'DELETE' })}
                >
                    <Image src={images.close} alt="DELETE" />
                </CustomLink>
            </div>
        </div>
    ));

    useEffect(() => {
        (props.selectedContactStack?.length > 1 ||
            props.recipientList[0]?.contactRecipientDetails?.contactPoint?.label ||
            props.recipientList[0]?.contactPoint) &&
            setIsCategoryRemoved(false);
        !props.recipientList[0]?.organisationId &&
            props.selectedContactStack?.length === 0 &&
            !props.recipientList[0]?.contactRecipientDetails?.contactPoint?.label &&
            setIsCategoryRemoved(true);
        props.recipientList[0]?.organisationId &&
            props.selectedContactStack?.length === 0 &&
            setIsCategoryRemoved(false);
        props.selectedContactStack?.length === 0 && props.recipientList?.length === 0 && setIsCategoryRemoved(false);
    }, [props.recipientList, props.selectedContactStack]);

    const checkCategory = async () => {
        const contGroupContacts = await getContactGroupsAndContactPointsByOrganisationId(
            props.organisationId,
            getTarget(),
            props.recipientList[0]?.name
        );

        !(contGroupContacts.contactGroups.items.length === 0 && contGroupContacts.contactPoints.items.length === 0) &&
            setIsCategoryRemoved(true);
    };

    useEffect(() => {
        if (
            props.organisationId &&
            props.recipientList[0]?.organisationId &&
            !props.recipientList[0]?.contactRecipientDetails
        ) {
            checkCategory();
        }
    }, [props.organisationId]);

    return (
        <div
            className={`${styles.contactPointsWrapper} ${
                props.isFromForward && props.isSelectRecipient && props.recipientList?.length === 0 && styles.noBorder
            } ${props.isFromForward && styles.isForward}`}
        >
            {props.isFromForward
                ? recipientRowList
                : (!showSelectInput ||
                      props.selectedContactStack?.length > 0 ||
                      props.recipientList?.[0]?.contactRecipientDetails) && (
                      <>
                          {getNewMailRecipient()}
                          {!isCategoryRemoved &&
                              (props.selectedContactStack?.length > 0 ||
                                  props.recipientList[0]?.contactRecipientDetails?.contactPoint?.label) &&
                              getNewMailRecipient(true)}
                      </>
                  )}

            {(showSelectInput || isCategoryRemoved) && (
                <div
                    className={`w-100 d-flex justify-content-between ${
                        props.recipientList?.length > 0 && !isCategoryRemoved ? 'pb-2 pr-2' : ''
                    } ${!props.isFromForward && 'pb-2'}`}
                    style={{ cursor: 'text' }}
                    onClick={(): void => onChooseClick()}
                >
                    <Input
                        className={`${styles.dummyInput} pl-2`}
                        disabled
                        name="chooseRecipientInput"
                        placeholder={isCategoryRemoved ? 'TO_CATEGORY' : 'TO_RECIPIENT'}
                        ariaLabel="TO_RECIPIENT"
                        value=""
                    />
                    <Button
                        className="m-0"
                        type="button"
                        ariaLabel={formatMessage({ id: 'CHOOSE_RECIPIENT' })}
                        disabled={props.isSelectRecipient}
                        onClick={(): void => onChooseClick()}
                    >
                        <FormattedMessage id="SELECT" />
                    </Button>
                </div>
            )}
        </div>
    );
};
